import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeftRight, Loader2, Mic, MicOff, Camera, X, Zap, Copy, Check } from 'lucide-react';
import Tesseract from 'tesseract.js';

// Max characters for input
const MAX_CHARS = 600;

export default function Translator() {
  const [sourceText, setSourceText] = useState('');
  const [translatedText, setTranslatedText] = useState('');
  const [sourceLang, setSourceLang] = useState('auto');
  const [targetLang, setTargetLang] = useState('es');
  const [loading, setLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [recognition, setRecognition] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [stream, setStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [copied, setCopied] = useState(false);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const WORKER_URL = 'https://translator-app.yyk-1borneo.workers.dev';

  const languages = [
    { code: 'auto', name: '🔍 Auto', speechCode: 'en-US', ocrCode: 'eng' },
    { code: 'en', name: 'English', speechCode: 'en-US', ocrCode: 'eng' },
    { code: 'es', name: 'Spanish', speechCode: 'es-ES', ocrCode: 'spa' },
    { code: 'fr', name: 'French', speechCode: 'fr-FR', ocrCode: 'fra' },
    { code: 'de', name: 'German', speechCode: 'de-DE', ocrCode: 'deu' },
    { code: 'it', name: 'Italian', speechCode: 'it-IT', ocrCode: 'ita' },
    { code: 'pt', name: 'Portuguese', speechCode: 'pt-PT', ocrCode: 'por' },
    { code: 'ru', name: 'Russian', speechCode: 'ru-RU', ocrCode: 'rus' },
    { code: 'ja', name: 'Japanese', speechCode: 'ja-JP', ocrCode: 'jpn' },
    { code: 'ko', name: 'Korean', speechCode: 'ko-KR', ocrCode: 'kor' },
    { code: 'zh', name: 'Chinese', speechCode: 'zh-CN', ocrCode: 'chi_sim' },
    { code: 'ar', name: 'Arabic', speechCode: 'ar-SA', ocrCode: 'ara' },
    { code: 'hi', name: 'Hindi', speechCode: 'hi-IN', ocrCode: 'hin' },
    { code: 'ms', name: 'Malay', speechCode: 'ms-MY', ocrCode: 'msa' },
    { code: 'nl', name: 'Dutch', speechCode: 'nl-NL', ocrCode: 'nld' },
    { code: 'pl', name: 'Polish', speechCode: 'pl-PL', ocrCode: 'pol' },
    { code: 'tr', name: 'Turkish', speechCode: 'tr-TR', ocrCode: 'tur' },
    { code: 'th', name: 'Thai', speechCode: 'th-TH', ocrCode: 'tha' },
    { code: 'vi', name: 'Vietnamese', speechCode: 'vi-VN', ocrCode: 'vie' },
    { code: 'id', name: 'Indonesian', speechCode: 'id-ID', ocrCode: 'ind' },
    { code: 'uk', name: 'Ukrainian', speechCode: 'uk-UA', ocrCode: 'ukr' },
    { code: 'cs', name: 'Czech', speechCode: 'cs-CZ', ocrCode: 'ces' },
    { code: 'sv', name: 'Swedish', speechCode: 'sv-SE', ocrCode: 'swe' },
    { code: 'da', name: 'Danish', speechCode: 'da-DK', ocrCode: 'dan' },
    { code: 'fi', name: 'Finnish', speechCode: 'fi-FI', ocrCode: 'fin' },
    { code: 'no', name: 'Norwegian', speechCode: 'no-NO', ocrCode: 'nor' },
  ];

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      const instance = new SpeechRecognition();
      instance.continuous = true;
      instance.interimResults = true;
      instance.onresult = (e) => {
        let text = '';
        for (let i = e.resultIndex; i < e.results.length; i++) {
          if (e.results[i].isFinal) text += e.results[i][0].transcript;
        }
        if (text) setSourceText(prev => prev + ' ' + text);
      };
      instance.onend = () => setIsListening(false);
      setRecognition(instance);
    }
  }, []);

  const toggleListening = () => {
    if (!recognition) return alert("Browser not supported");
    if (isListening) {
      recognition.stop();
    } else {
      const langObj = languages.find(l => l.code === sourceLang);
      recognition.lang = langObj?.speechCode || 'en-US';
      recognition.start();
      setIsListening(true);
    }
  };

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      setStream(mediaStream);
      setShowCamera(true);
      setTimeout(() => { if (videoRef.current) videoRef.current.srcObject = mediaStream; }, 150);
    } catch (err) { alert("Camera Error"); }
  };

  const capturePhoto = () => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    setCapturedImage(canvas.toDataURL('image/jpeg'));
    if (stream) stream.getTracks().forEach(t => t.stop());
  };

  const extractText = async () => {
    setLoading(true);
    try {
      const ocrLang = languages.find(l => l.code === sourceLang)?.ocrCode || 'eng';
      const worker = await Tesseract.createWorker(ocrLang);
      const { data: { text } } = await worker.recognize(capturedImage);
      setSourceText(text);
      setShowCamera(false);
      setCapturedImage(null);
      await worker.terminate();
    } catch (e) { alert("OCR Failed"); }
    finally { setLoading(false); }
  };

  const handleClearAll = () => {
    setSourceText('');
    setTranslatedText('');
    setCopied(false);
  };

  const handleCopy = () => {
    if (!translatedText) return;
    navigator.clipboard.writeText(translatedText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleTranslate = async () => {
    if (!sourceText.trim()) return;
    setLoading(true);
    
    try {
      const response = await fetch(`${WORKER_URL}/translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ q: sourceText, source: sourceLang, target: targetLang }),
      });
      
      const data = await response.json();
      
      if (data.translatedText) {
        setTranslatedText(data.translatedText);
      } else if (data.error) {
        setTranslatedText(`Error: ${data.error}`);
      } else {
        setTranslatedText("Translation Failed");
      }
    } catch (e) {
      setTranslatedText(`Error: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const swap = () => {
    if (sourceLang === 'auto') return;
    const oldSourceLang = sourceLang;
    const oldSourceText = sourceText;
    setSourceLang(targetLang);
    setTargetLang(oldSourceLang);
    setSourceText(translatedText);
    setTranslatedText(oldSourceText);
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb', display: 'flex', flexDirection: 'column', margin: 0, padding: 0 }}>
      {/* Header */}
      <div style={{ backgroundColor: 'white', borderBottom: '1px solid #e5e7eb', padding: '10px 14px', textAlign: 'center' }}>
        <h1 style={{ fontSize: '18px', fontWeight: 'bold', margin: 0, lineHeight: '1.2' }}>🌍 Translator</h1>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, padding: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        
        {/* Language Row */}
        <div style={{ backgroundColor: 'white', padding: '6px 8px', borderRadius: '10px', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
          <select 
            value={sourceLang} 
            onChange={(e) => setSourceLang(e.target.value)} 
            style={{ flex: '0 0 120px', fontSize: '13px', fontWeight: 'bold', border: 'none', outline: 'none', backgroundColor: 'transparent', padding: '4px 2px' }}
          >
            {languages.map(l => <option key={l.code} value={l.code}>{l.name}</option>)}
          </select>
          
          <button 
            onClick={swap} 
            style={{ margin: '0.4px', padding: '6px', backgroundColor: '#dbeafe', borderRadius: '50%', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
          >
            <ArrowLeftRight size={18} color="#2563eb" />
          </button>
          
          <select 
            value={targetLang} 
            onChange={(e) => setTargetLang(e.target.value)} 
            style={{ flex: '0 0 120px', fontSize: '13px', fontWeight: 'bold', border: 'none', outline: 'none', backgroundColor: 'transparent', textAlign: 'left' }}
          >
            {languages.filter(l => l.code !== 'auto').map(l => <option key={l.code} value={l.code}>{l.name}</option>)}
          </select>
        </div>

        {/* Input Area */}
        <div style={{ position: 'relative', backgroundColor: 'white', borderRadius: '16px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <textarea
            rows={10}
            value={sourceText}
            onChange={(e) => {
             const text = e.target.value;
             if (text.length <= MAX_CHARS) setSourceText(text);
            }}
 
            style={{ width: '100%', padding: '12px', paddingBottom: '45px', fontSize: '14px', border: 'none', outline: 'none', resize: 'none', boxSizing: 'border-box' }} 
            placeholder="Type or speak here..."
          />
          {/* Character Counter */}
          <div
            style={{
              position: 'absolute',
              bottom: '12px',
              left: '12px',
              fontSize: '12px',
              color: sourceText.length >= MAX_CHARS ? '#ef4444' : '#6b7280',
              fontWeight: sourceText.length >= MAX_CHARS ? 'bold' : 'normal',
              pointerEvents: 'none',
              zIndex: 10,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              padding: '2px 6px',
              borderRadius: '4px',
            }}
          >
            {sourceText.length} / {MAX_CHARS} chars
          </div>
          <div style={{ position: 'absolute', bottom: '12px', right: '12px', display: 'flex', gap: '8px' }}>
            {sourceText && (
              <button 
                onClick={handleClearAll} 
                style={{ padding: '8px', backgroundColor: '#f3f4f6', borderRadius: '50%', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <X size={20} color="#ef4444" />
              </button>
            )}
            <button 
              onClick={toggleListening} 
              style={{ padding: '8px', backgroundColor: isListening ? '#ef4444' : '#f3f4f6', borderRadius: '50%', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              {isListening ? <MicOff size={20} color="white" /> : <Mic size={20} color="#6b7280" />}
            </button>
            <button 
              onClick={startCamera} 
              style={{ padding: '8px', backgroundColor: '#f3f4f6', borderRadius: '50%', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <Camera size={20} color="#6b7280" />
            </button>
          </div>
        </div>

        {/* Output Area */}
        <div style={{ position: 'relative', backgroundColor: '#f3f4f6', borderRadius: '16px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <textarea 
            rows={10} 
            readOnly 
            value={translatedText} 
            style={{ width: '100%', padding: '12px', fontSize: '14px', border: 'none', outline: 'none', resize: 'none', backgroundColor: 'transparent', boxSizing: 'border-box' }} 
            placeholder="Translation will appear here..."
          />
          {translatedText && !loading && (
            <button 
              onClick={handleCopy} 
              style={{ position: 'absolute', bottom: '12px', right: '12px', padding: '8px', backgroundColor: 'rgba(255,255,255,0.8)', borderRadius: '50%', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              {copied ? <Check size={20} color="#22c55e" /> : <Copy size={20} color="#2563eb" />}
            </button>
          )}
          {loading && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.5)' }}>
              <Loader2 className="animate-spin" size={32} color="#2563eb" />
            </div>
          )}
        </div>

        {/* Translate Button */}
        <button 
          onClick={handleTranslate} 
          disabled={loading || !sourceText.trim()} 
          style={{ width: '100%', backgroundColor: loading || !sourceText.trim() ? '#d1d5db' : '#2563eb', color: 'white', fontWeight: 'bold', padding: '12px 16px', borderRadius: '16px', border: 'none', cursor: loading || !sourceText.trim() ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: '15px' }}
        >
          {loading ? (
            <Loader2 className="animate-spin" size={20} />
          ) : (
            <>
              <Zap size={20} fill="currentColor" /> 
              Translate Now
            </>
          )}
        </button>
      </div>

      {/* Camera Modal */}
      {showCamera && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'black', zIndex: 50, display: 'flex', flexDirection: 'column' }}>
          <button 
            onClick={() => {
              setShowCamera(false); 
              if(stream) stream.getTracks().forEach(t => t.stop());
            }} 
            style={{ position: 'absolute', top: '20px', right: '20px', color: 'white', zIndex: 51, backgroundColor: 'transparent', border: 'none', cursor: 'pointer' }}
          >
            <X size={32} />
          </button>
          
          {!capturedImage ? (
            <div style={{ position: 'relative', flex: 1 }}>
              <video ref={videoRef} autoPlay playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              <button 
                onClick={capturePhoto} 
                style={{ position: 'absolute', bottom: '40px', left: '50%', transform: 'translateX(-50%)', width: '80px', height: '80px', backgroundColor: 'white', borderRadius: '50%', border: '8px solid #9ca3af', cursor: 'pointer' }} 
              />
            </div>
          ) : (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px', backgroundColor: '#111827' }}>
              <img src={capturedImage} style={{ maxHeight: '60vh', borderRadius: '16px', border: '2px solid rgba(255,255,255,0.2)' }} alt="captured" />
              <button 
                onClick={extractText} 
                style={{ width: '100%', backgroundColor: '#2563eb', color: 'white', padding: '16px', borderRadius: '12px', marginTop: '32px', fontWeight: 'bold', border: 'none', cursor: 'pointer', fontSize: '16px' }}
              >
                ✨ Extract Text
              </button>
              <button 
                onClick={() => setCapturedImage(null)} 
                style={{ marginTop: '16px', color: 'rgba(255,255,255,0.5)', backgroundColor: 'transparent', border: 'none', cursor: 'pointer' }}
              >
                Retake Photo
              </button>
            </div>
          )}
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </div>
      )}
    </div>
  );
}